import { type UseFetchOptions } from 'nuxt/app'
import defu from 'defu'
import { joinURL } from 'ufo'

import { encrypt_bson } from '~/utils/secure_messaging'

import HybridCrypto from '~/utils/hybrid_crypto'

type OtkResponse = {
  success: boolean
  public_key: string
  uuid: string
}

export const useApi = () => {
  const config = useRuntimeConfig()
  const base_url = config.public.api_url || 'https://api.admin.tg.pintopay.dev'

  const fetchWrapper = async <R>(endpoint: string, options: UseFetchOptions<any> = {}) => {
    const authStore = useAuthStore()
    const token = authStore.token

    const defaults: UseFetchOptions<R> = {
      baseURL: base_url,
      onResponseError: ({ response }) => {
        if (response.status === 401) {
          navigateTo('/?error=403')
        }
        if (response.status === 403) {
          navigateTo('/?error=403')
        }
      }
    }

    if (token) {
      defaults.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    }

    const finalOptions: UseFetchOptions<R> = defu(options, defaults)

    return $fetch<R>(endpoint, finalOptions as any)
  }

  return {
    // GET запрос
    get: <T>(endpoint: string, data: any = {},  options: UseFetchOptions<T> = {}) => {
      return fetchWrapper<T>(endpoint, { ...options, method: 'GET', query: data })
    },

    // POST запрос
    post: <T>(endpoint: string, data: any = {}, options: UseFetchOptions<T> = {}) => {
      return fetchWrapper<T>(endpoint, {
        ...options,
        method: 'POST',
        body: data
      })
    },

    post_form: <T>(endpoint: string, form_data: any = {}, options: UseFetchOptions<T> = {}) => {
      return fetchWrapper<T>(endpoint, {
        ...options,
        method: 'POST',
        body: form_data
      })
    },

    post_secure: async <T>(endpoint: string, data: any = {}, options: UseFetchOptions<T> = {}) => {

      const otk_response = await fetchWrapper('/api/secure/otk', { ...options, method: 'POST' })

      // console.log('otk_response',otk_response)

      const body = {
        data: await encrypt_bson(otk_response.public_key, data),
        uuid: otk_response.uuid
      }

      // console.log('body',body)

      return fetchWrapper<T>(endpoint, {
        ...options,
        method: 'POST',
        body: body,
      })
    },

    post_hybrid_otk: async <T>(endpoint: string, data: any = {}, options: UseFetchOptions<T> = {}) => {

      const server_otk_response = await fetchWrapper<OtkResponse>('/api/secure/otk', { ...options, method: 'POST' })

      // console.log('server_otk_response',server_otk_response)

      const randomBytes = new Uint8Array(32)
			crypto.getRandomValues(randomBytes)
			const client_otk_decrypted = btoa(String.fromCharCode.apply(null, [...randomBytes]))

      const data_encrypted = await HybridCrypto.hybridEncrypt(
				atob(server_otk_response.public_key),
				JSON.stringify(data),
				client_otk_decrypted,
			)

      const body = {
        data: data_encrypted.encryptedData,
        client_otk: data_encrypted.encryptedPassword,
        uuid: server_otk_response.uuid
      }

      // console.log('body',body)

      return fetchWrapper<T>(endpoint, {
        ...options,
        method: 'POST',
        body: body,
      })
    },

    // PUT запрос
    put: <T>(endpoint: string, data: any, options: UseFetchOptions<T> = {}) => {
      return fetchWrapper<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: data
      })
    },

    // DELETE запрос
    delete: <T>(endpoint: string, options: UseFetchOptions<T> = {}) => {
      return fetchWrapper<T>(endpoint, { ...options, method: 'DELETE' })
    },

    get_receipt_image_url: (file_name: string) => {
      return joinURL(base_url, `/api/client/fg/receipt_image/${file_name}`)
    }
  }

  
}